import React, { useContext } from 'react';
import c from './custom-theme.module.scss';
import { ThemeContext } from '@/components/providers/theme-provider/ThemeProvider';
import { ImageUpload } from '@monash/portal-react';
import { APIContext } from '@monash/portal-frontend-common';
import { useSnackbar } from '@/components/providers/SnackbarProvider';
import * as Sentry from '@sentry/browser';

const UploadWrapper = () => {
  const {
    updateCustomTheme,
    customThemeVariables,
    wallpaperList,
    loading,
    setLoading,
    wallpaperImageURL,
    setWallpaperImageURL,
    wallpaperFileName,
    setWallpaperFileName,
    uploaderIsLoading,
  } = useContext(ThemeContext);
  const { deleteCustomImage, getCustomImage, postCustomImage } =
    useContext(APIContext);
  const { addSnackbar } = useSnackbar();

  const isSelected =
    wallpaperImageURL !== null &&
    (customThemeVariables.wallpaper === `url('${wallpaperImageURL}')` ||
      // in the event we come back to customise tab and the custom image is uploaded, it must have selected styling
      (customThemeVariables.wallpaper !== null &&
        !wallpaperList
          .map((w) => `url('${w}')`)
          .includes(customThemeVariables.wallpaper)));

  const uploadImage = async (fileName, encodedValue) => {
    setLoading(true);
    postCustomImage(fileName, 'wallpaper', encodedValue)
      .then(() => getCustomImage('wallpaper'))
      .then((imgurl) => {
        updateCustomTheme([
          { attribute: 'imageFileName', value: fileName },
          { attribute: 'wallpaper', value: `url('${imgurl}')` },
        ]);
        setWallpaperImageURL(imgurl);
        setWallpaperFileName(fileName);
      })
      .catch((error) => {
        const errorCode = error.status ? error.status.toString() : '';
        switch (errorCode[0]) {
          case '4':
            addSnackbar({
              message:
                'This file cannot be accepted - please use a different file ',
              type: 'error',
            });
            Sentry.captureException(error);
            break;
          default:
            addSnackbar({
              message:
                'We’re not able to save your image right now – please try again later',
              type: 'error',
            });
            Sentry.captureException(error);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const deleteImage = () => {
    setLoading(true);
    deleteCustomImage('wallpaper')
      .then((result) => {
        if (
          customThemeVariables.wallpaper === `url('${wallpaperImageURL}')` ||
          // in the event we come back to customise tab and the custom image is uploaded, it must have selected styling
          (customThemeVariables.wallpaper !== null &&
            !wallpaperList
              .map((w) => `url('${w}')`)
              .includes(customThemeVariables.wallpaper))
        ) {
          updateCustomTheme([
            { attribute: 'wallpaper', value: null },
            { attribute: 'imageFileName', value: null },
          ]);
        }
        setWallpaperImageURL(null);
        setWallpaperFileName(null);
      })
      .catch((error) => {
        addSnackbar({
          message:
            'We’re not able to remove your image right now – please try again later',
          type: 'error',
        });
        Sentry.captureException(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const selectImage = () => {
    updateCustomTheme([
      { attribute: 'wallpaper', value: `url('${wallpaperImageURL}')` },
    ]);
  };

  return (
    <ImageUpload
      className={[c.uploadWrapper, isSelected ? c.uploadSelected : ''].join(
        ' '
      )}
      title=""
      uploadPrompt="Upload custom wallpaper"
      onUpload={uploadImage}
      onDelete={deleteImage}
      onSelect={selectImage}
      imageSelectDisabled={false}
      imageURL={wallpaperImageURL}
      fileName={wallpaperFileName}
      maxFileSize={7}
      isLoading={loading || uploaderIsLoading}
      ariaLabel="Upload custom wallpaper, "
    />
  );
};

export default UploadWrapper;
